// "If nothing changes" projection (FM-11 / FM-12).
//
// Kept client-side and out of the API on purpose: this is a planning scenario
// drawn from the measured trend, not a modelled output the server should be
// asserting. The assumption string below is rendered next to every chart that
// uses it.

import type { FatigueProjection, FatigueProjectionPoint, FatigueTrendPoint } from './types';

const KEYS = ['fed', 'lrm', 'cdi', 'pdc'] as const;

export const PROJECTION_ASSUMPTION =
  'Projection continues the trend of the last six weeks with no schedule, workload or rest changes. It is a scenario for planning, not a forecast, and it is damped further out because confidence drops with distance.';

/**
 * Extends a measured trend 12 weeks forward on a "nothing changes" basis: the
 * slope of the recent history continues, damped as it goes out so the far end
 * doesn't imply false precision.
 *
 * Buckets the API flagged as empty are dropped first — projecting a coverage gap
 * would read as a real improvement.
 */
export const buildProjection = (history: FatigueTrendPoint[]): FatigueProjection => {
  const measuredHistory = history.filter((point) => !point.empty);

  const blocks = [
    { label: 'Weeks 1–2', weekOffsets: [2] },
    { label: 'Weeks 3–4', weekOffsets: [4] },
    { label: 'Weeks 5–6', weekOffsets: [6] },
    { label: 'Weeks 7–8', weekOffsets: [8] },
    { label: 'Weeks 9–10', weekOffsets: [10] },
    { label: 'Weeks 11–12', weekOffsets: [12] },
  ];

  // Two measured points is the minimum for a slope. With fewer, show the history
  // and no forward line rather than extending a single reading flat.
  if (measuredHistory.length < 2) {
    return {
      points: measuredHistory.map((point, i) => ({
        ...point,
        projected: false,
        weekOffset: -2 * (measuredHistory.length - 1 - i),
      })),
      blocks,
      assumption: 'Not enough measured history to project forward yet.',
    };
  }

  const last = measuredHistory[measuredHistory.length - 1];
  const prior = measuredHistory[Math.max(0, measuredHistory.length - 3)];

  // History is spaced two weeks apart, so the measured offsets run -12…0.
  const measured: FatigueProjectionPoint[] = measuredHistory.map((point, i) => ({
    ...point,
    projected: false,
    weekOffset: -2 * (measuredHistory.length - 1 - i),
  }));

  // Slope per fortnight across the last three measured points.
  const steps = Math.max(1, measuredHistory.length - 1 - Math.max(0, measuredHistory.length - 3));
  const slope = KEYS.reduce<Record<string, number>>((acc, key) => {
    acc[key] = (last[key] - prior[key]) / steps;
    return acc;
  }, {});

  const projected: FatigueProjectionPoint[] = [2, 4, 6, 8, 10, 12].map((week, step) => {
    // Damping: each fortnight out contributes less than the one before.
    const damping = 1 / (1 + step * 0.35);
    const project = (key: (typeof KEYS)[number]) =>
      Math.max(0, Math.round(last[key] + slope[key] * (step + 1) * damping));
    return {
      period: `Wk+${week}`,
      fed: project('fed'),
      lrm: project('lrm'),
      cdi: project('cdi'),
      pdc: project('pdc'),
      projected: true,
      weekOffset: week,
    };
  });

  return { points: [...measured, ...projected], blocks, assumption: PROJECTION_ASSUMPTION };
};
